import { Bar } from "react-chartjs-2";
import { Fragment, useEffect, useRef, useState } from "react";
import { Child, Container, Header, LayerContent } from "../style";
import { TableContainer } from "../../../Users/User.style";
import { ChartContainer, NoData } from "../../style";
import { useAnalyticsWrapper } from "../../../../../store/store";
import useAnalytics from "../../../../../hooks/useAnalytics";
import moment from "moment";
import {
  barOptions,
  getMetrics,
  doTransformData,
  woundIncidentUnitPlaceCols,
  handlePointClicked,
  transformDataForBarChart,
  prepareTableData,
  prepareChartData,
  formatKnownStrings,
  lineOptions,
  reverseFormatKnownStrings,
  convertObjectToChartData,
} from "../formUtils";
import CustomerLoader from "../../../../../components/CustomLoader";
import SectionModal, { DefaultContainer } from "./SectionModal";
import { Line, getElementAtEvent } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  BarElement,
  LineElement,
  Tooltip,
} from "chart.js";
import {
  handleCSVDownload,
  handlePDFDownload,
} from "../../../../../utils/helpers/pdfCsvUtils";
import { woundAnalysisDetailCols } from "../../dummyData/colums";
import { Grid, GridColumn, GridColumnProps } from '@progress/kendo-react-grid';
import { CenteredWholeNumCell, HeaderCenteredCell } from "../../../../../components/DynamicGrid/customCells";
import { TotalRow } from "../../../../../components/DynamicGrid/customRows";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Tooltip
);

const SkinIncidentTrend = ({
  setClickedIncident,
  isFetchingSelectedIncident,   
}: any) => {
  const [tableData, setTableData] = useState<any>([]);
  const [grid, setGrid] = useState<any>([]);    
  const [month, setMonth] = useState("");
  const [showMonthModal, setShowMonthModal] = useState(false);
  const [areaDetails, setAreaDetails] = useState<any>([]);
  const [chartData, setChartData] = useState<any>([]);
  const lineRef = useRef<any>(null);

  const { startDate, endDate, woundFacility, woundAnalysisIncidents } = useAnalyticsWrapper((state) => ({
    startDate: state.startDate,
    endDate: state.endDate,
    woundFacility: state.woundFacility,
    woundAnalysisIncidents: state.woundAnalysisIncidents
  }));

  type TrendSummaryRow = {
    summaryType: string;
    percentage: number;
    count: number;
  };

  const summaryTableData = (summaryData: any) => {
    let returnData: TrendSummaryRow[] = [];
    if (summaryData) {
      const puCount = summaryData.pressureUlcerCount ?? 0;
      const puPct = summaryData.pressureUlcerPercentage ?? 0;
      const total = summaryData.totalIncidents ?? 0;
      const row: TrendSummaryRow = {
        summaryType: 'Pressure Ulcers',
        percentage: puPct,
        count: puCount,
      };
      returnData.push(row);
      const otherRow: TrendSummaryRow = {
        summaryType: "Other",
        percentage: 100 - puPct,
        count: total - puCount,
      };
        returnData.push(otherRow);
    const totalRow: TrendSummaryRow = {
        summaryType: "Total",
        percentage: 100,
        count: total,
      };
      returnData.push(totalRow);
    }
    return returnData;
  }

  type MonthlyTrendDataType = {
    pressureUlcerWounds: number;
    month: string;
    totalWounds: number;
  }

  const genChartData = (monthData: MonthlyTrendDataType[]) => {
    const datasets: any[] = [];
    let labels: any[] = [];
    if (monthData.length) {
      // Find date range
      const firstMonth = moment(monthData[0].month);
      const lastMonth = moment(monthData[monthData.length - 1].month);
      
      // Create lookup map for existing data
      const dataMap = new Map(
        monthData.map((item: any) => [
          moment(item.month).format('YYYY-MM'),
          item
        ])
      );
      
      let pressureUlcerData: any[] = [];
      let otherData: any[] = [];
      let totalData: any[] = [];

      // Generate complete range
      let current = firstMonth.clone();
      while (current.isSameOrBefore(lastMonth, 'month')) {
        const key = current.format('YYYY-MM');
        const existing = dataMap.get(key);
        if (existing) {

          labels.push(moment(existing.month).format('MMMM YYYY'));
          pressureUlcerData.push(existing.pressureUlcerWounds);
          otherData.push(existing.totalWounds - existing.pressureUlcerWounds);
          totalData.push(existing.totalWounds);
        } else {
          labels.push(current.format('MMMM YYYY'));
          pressureUlcerData.push(0);
          otherData.push(0);
          totalData.push(0);
        }
        current.add(1, 'month');
      }

      datasets.push({
        label: "Pressure Ulcers",
        data: pressureUlcerData,
        fill: false,
        borderColor: "#FF6384",
        pointBorderWidth: 5,
        pointRadius: 10,
        pointHoverRadius: 15,
        tension: 0.4,
        pointHoverBorderWidth: 5,
        yAxisID: "y",
      });
      datasets.push({
        label: "Other Wounds",
        data: otherData,
        fill: false,
        borderColor: "#FFCE56",
        pointBorderWidth: 5,
        pointRadius: 10,
        pointHoverRadius: 15,
        tension: 0.4,
        pointHoverBorderWidth: 5,
        yAxisID: "y",
      });
      datasets.push({
        label: "Total Wounds",
        data: totalData,
        fill: false,
        borderColor: "#36A2EB",
        pointBorderWidth: 5,
        pointRadius: 10,
        pointHoverRadius: 15,
        tension: 0.4,
        pointHoverBorderWidth: 5,
        yAxisID: "y",
      });
    }
    return { labels, datasets };
  }


  useEffect(() => {
    if (woundAnalysisIncidents !== undefined) {
      const tempData = woundAnalysisIncidents?.skinIncidentTrend?.summary;
      const transformedData = summaryTableData(tempData);
      setTableData(transformedData);

      const chartTempData = woundAnalysisIncidents?.skinIncidentTrend?.monthlyTrend ?? [];
      let formattedChartData = genChartData(chartTempData);
      setChartData(formattedChartData);
      setAreaDetails(woundAnalysisIncidents?.allIncidents);
    }
  }, [woundAnalysisIncidents]);
  
  const title = "Monthly Trend — Total Wounds vs Pressure Ulcers vs Other";

  const handleChartClicked = (element: any) => {
    if (element) {
      const index = element.index;
      const datasetIndex = element.datasetIndex;
     
      const date = chartData?.labels[index];
      setMonth(date);
      const filterDate = moment(date, 'MMMM YYYY').format('YYYY-MM');
      const filteredDetails = woundAnalysisIncidents.skinIncidentTrend.drillDownByMonth[filterDate] || [];
      
      let areaDrilldown = filteredDetails;
      if (datasetIndex === 0){
        areaDrilldown = filteredDetails.filter((ele: any) => ele.skinIssueTypeUlcer === "Pressure Ulcer");
      }
      else if (datasetIndex === 1){
        areaDrilldown = filteredDetails.filter((ele: any) => ele.skinIssueTypeUlcer !== "Pressure Ulcer");
      }

      setAreaDetails(areaDrilldown);
      setShowMonthModal(true);
    }
  };
  
  useEffect(() => {
    let cellCountProps = { cells: { data: CenteredWholeNumCell, headerCell: HeaderCenteredCell } };
    let cellPercentageProps = { cells: { data: CenteredWholeNumCell, append: '%', headerCell: HeaderCenteredCell } };
    const grid = (
      <Fragment>
        <Grid
          data={tableData || []}
          scrollable="none"
          rows={{ data: TotalRow }}
      >
        <GridColumn field="summaryType" title="Type" />
        <GridColumn field="count" title="# of Incidents" {...cellCountProps as GridColumnProps}/>
        <GridColumn field="percentage" title="%" {...cellPercentageProps as GridColumnProps}/>
        </Grid>
      </Fragment>
    );
    setGrid(grid);
  }, [tableData]); 

   return (
    <LayerContent>
      <Header>{title}</Header>
        <Container>
          <Child flex="40%">
            {grid}
          </Child>
          <Child flex="60%">
            <ChartContainer>
              {chartData?.labels?.length > 0 ? (
                <Line
                  ref={lineRef}
                  data={chartData}
                  options={lineOptions}
                  onClick={(event) => {
                    const clickedElement = getElementAtEvent(
                      lineRef.current,
                      event
                    )[0];
                    handleChartClicked(clickedElement);
                  }}
                />
              ) : (
                <NoData>No Data</NoData>
              )}
            </ChartContainer>
          </Child>
        </Container>
      {showMonthModal ? (
        <SectionModal
          open={showMonthModal}
          facilityName={(woundFacility?.label ?? woundFacility?.[0]?.label)}
          startDate={startDate}
          endDate={endDate}
          closeFn={() => setShowMonthModal(false)}
          resetFn={() => {}}
          isLoading={false}
          tableData={areaDetails}
          tableCols={woundAnalysisDetailCols()}
          title={`${title} - ${month}`}
          getUserCallback={(item: any) => setClickedIncident(item)}
          handlePdfExport={() =>
            handlePDFDownload({
              data: areaDetails,
              columnData: woundAnalysisDetailCols(),
              fileName: (woundFacility?.label ?? woundFacility?.[0]?.label),
              startDate,
              endDate,
              subTitle: `${title} - ${month}`,
            })
          }
          handleCsvExport={() =>
            handleCSVDownload(
              areaDetails,
              woundAnalysisDetailCols(),
              `${(woundFacility?.label ?? woundFacility?.[0]?.label)} - ${title} - ${month}`
            )
          }
        />
      ) : null}
    </LayerContent>
  );
};

export default SkinIncidentTrend;